import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Badge,
  Table,
  Modal,
  Alert,
  Spinner,
  InputGroup,
  Dropdown,
  DropdownButton,
  Pagination,
  Nav,
  Tab,
} from "react-bootstrap";
import { DataGrid } from "@mui/x-data-grid";
import { CSVLink } from "react-csv";
import { useAuth } from "../contexts/AuthContext";
import { fires } from "../firebase";
import { useNavigate, Link } from "react-router-dom";

export default function UserManagement() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(20);
  const [activeTab, setActiveTab] = useState("users");

  // User details states
  const [userEvents, setUserEvents] = useState([]);
  const [userRegistrations, setUserRegistrations] = useState([]);
  const [userOrders, setUserOrders] = useState([]);

  // Analytics states
  const [userAnalytics, setUserAnalytics] = useState({
    totalUsers: 0,
    activeUsers: {
      daily: 0,
      weekly: 0,
      monthly: 0
    },
    newUsers: {
      today: 0,
      thisWeek: 0,
      thisMonth: 0
    },
    usersByRole: {},
    userTrend: [],
    monthlyNewUsers: []
  });

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    try {
      const adminDoc = await fires.collection('admin').doc(currentUser.uid).get();
      if (!adminDoc.exists) {
        navigate('/');
        return;
      }
      
      const adminData = adminDoc.data();
      if (!adminData.roles || !adminData.roles.includes('management')) {
        navigate('/');
        return;
      }
      
      // User has management role, load users
      loadUsers();
    } catch (err) {
      console.error('Error checking admin access:', err);
      navigate('/');
    }
  };

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, filterStatus]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const usersSnapshot = await fires.collection("users").get();
      const usersData = [];
      
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        usersData.push({
          id: userDoc.id,
          ...userData,
          createdAt: userData.timestamp?.toDate?.() || new Date(1970, 0, 1),
          timestamp: userData.timestamp?.toDate?.() || new Date(1970, 0, 1),
          lastLogin: userData.timestamp?.toDate?.() || new Date(1970, 0, 1),
        });
      }
      
      // Sort users by timestamp in descending order (newest first)
      usersData.sort((a, b) => b.timestamp - a.timestamp);
      
      setUsers(usersData);
      calculateUserAnalytics(usersData);
    } catch (err) {
      setError("Failed to load users: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateUserAnalytics = (usersData) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    // Use actual current month (from 1st of current month) instead of rolling 30 days
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    // Debug: Check users with timestamp today (excluding 1970 fallback)
    const todayUsers = usersData.filter(user => {
      const userTimestamp = user.timestamp;
      const fallbackDate = new Date(1970, 0, 1);
      return userTimestamp && userTimestamp >= today && userTimestamp.getTime() !== fallbackDate.getTime();
    });
    console.log('Users with timestamp today:', todayUsers.length);
    console.log('Today date:', today);
    console.log('Sample user timestamps:', todayUsers.slice(0, 3).map(u => ({ 
      email: u.email, 
      timestamp: u.timestamp,
      createdAt: u.createdAt 
    })));

    // Calculate active users based on lastLogin
    const activeUsers = {
      daily: usersData.filter(user => user.lastLogin && user.lastLogin >= today).length,
      weekly: usersData.filter(user => user.lastLogin && user.lastLogin >= weekAgo).length,
      monthly: usersData.filter(user => user.lastLogin && user.lastLogin >= currentMonthStart).length
    };

    // Calculate new users based on timestamp (only users with actual timestamp field, excluding 1970 fallback)
    const fallbackDate = new Date(1970, 0, 1);
    
    // Debug: Check what's being counted
    const thisMonthUsers = usersData.filter(user => {
      const userTimestamp = user.timestamp;
      return userTimestamp && userTimestamp >= currentMonthStart && userTimestamp.getTime() !== fallbackDate.getTime();
    });
    
    console.log('This month calculation:');
    console.log('Current month start date:', currentMonthStart);
    console.log('Users found this month:', thisMonthUsers.length);
    console.log('Sample this month users:', thisMonthUsers.slice(0, 5).map(u => ({
      email: u.email,
      timestamp: u.timestamp,
      timestampString: u.timestamp.toISOString()
    })));
    
    const newUsers = {
      today: usersData.filter(user => {
        const userTimestamp = user.timestamp;
        return userTimestamp && userTimestamp >= today && userTimestamp.getTime() !== fallbackDate.getTime();
      }).length,
      thisWeek: usersData.filter(user => {
        const userTimestamp = user.timestamp;
        return userTimestamp && userTimestamp >= weekAgo && userTimestamp.getTime() !== fallbackDate.getTime();
      }).length,
      thisMonth: thisMonthUsers.length
    };

    // Calculate users by role
    const usersByRole = {};
    usersData.forEach(user => {
      const role = user.orgId ? "org admin" : (user.role || "user");
      usersByRole[role] = (usersByRole[role] || 0) + 1;
    });

    // Calculate user trend (last 30 days) based on timestamp (excluding 1970 fallback)
    const userTrend = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today.getTime() - i * 24 * 60 * 60 * 1000);
      const nextDate = new Date(date.getTime() + 24 * 60 * 60 * 1000);
      const usersOnDate = usersData.filter(user => {
        const userTimestamp = user.timestamp;
        return userTimestamp && userTimestamp >= date && userTimestamp < nextDate && userTimestamp.getTime() !== fallbackDate.getTime();
      }).length;
      userTrend.push({
        date: date.toISOString().split('T')[0],
        count: usersOnDate
      });
    }

    // Monthly new users based on timestamp (users with actual timestamp, grouped by created month)
    const monthlyMap = new Map();
    usersData.forEach(user => {
      const ts = user.timestamp;
      if (!ts || ts.getTime() === fallbackDate.getTime()) return;
      const key = `${ts.getFullYear()}-${String(ts.getMonth() + 1).padStart(2, "0")}`;
      const label = ts.toLocaleString(undefined, { year: "numeric", month: "short" });
      const prev = monthlyMap.get(key) || { key, label, count: 0 };
      prev.count += 1;
      monthlyMap.set(key, prev);
    });
    const monthlyNewUsers = Array.from(monthlyMap.values()).sort((a, b) => a.key.localeCompare(b.key));

    setUserAnalytics({
      totalUsers: usersData.length,
      activeUsers,
      newUsers,
      usersByRole,
      userTrend,
      monthlyNewUsers
    });
  };

  const filterUsers = () => {
    let filtered = users;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.includes(searchTerm)
      );
    }

    // Filter by status
    if (filterStatus !== "all") {
      filtered = filtered.filter(user => user.status === filterStatus);
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  const loadUserDetails = async (userId) => {
    try {
      // Load user's events attended
      const eventsSnapshot = await fires
        .collection("eventLogs")
        .where("userId", "==", userId)
        .get();
      
      const eventsData = [];
      for (const eventDoc of eventsSnapshot.docs) {
        const eventData = eventDoc.data();
        eventsData.push({
          id: eventDoc.id,
          ...eventData,
          timestamp: eventData.timestamp?.toDate?.() || new Date(),
        });
      }
      setUserEvents(eventsData);

      // Load user's registrations
      const registrationsSnapshot = await fires
        .collection("eventMembers")
        .where("userId", "==", userId)
        .get();
      
      const registrationsData = [];
      for (const regDoc of registrationsSnapshot.docs) {
        const regData = regDoc.data();
        registrationsData.push({
          id: regDoc.id,
          ...regData,
          createdAt: regData.createdAt?.toDate?.() || new Date(),
        });
      }
      setUserRegistrations(registrationsData);

      // Load user's orders
      const ordersSnapshot = await fires
        .collection("orders")
        .where("userId", "==", userId)
        .get();
      
      const ordersData = [];
      for (const orderDoc of ordersSnapshot.docs) {
        const orderData = orderDoc.data();
        ordersData.push({
          id: orderDoc.id,
          ...orderData,
          createdAt: orderData.createdAt?.toDate?.() || new Date(),
        });
      }
      setUserOrders(ordersData);

    } catch (err) {
      setError("Failed to load user details: " + err.message);
    }
  };

  const handleUserClick = async (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
    await loadUserDetails(user.id);
  };

  const exportUsersToCSV = () => {
    return filteredUsers.map(user => ({
      Email: user.email || "",
      "First Name": user.firstName || "",
      "Last Name": user.lastName || "",
      "Phone Number": user.phone || "",
      Status: user.status || "active",
      "Created Date": user.createdAt ? user.createdAt.toLocaleDateString() : "",
      "Last Login": user.lastLogin ? user.lastLogin.toLocaleDateString() : "",
      "Organization": user.userOrg || "",
      "Role": user.role || "user",
    }));
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "success",
      inactive: "secondary",
      suspended: "danger",
      pending: "warning",
    };
    return <Badge bg={variants[status] || "secondary"}>{status || "active"}</Badge>;
  };

  const getRoleBadge = (role) => {
    const variants = {
      admin: "danger",
      superadmin: "dark",
      user: "primary",
      organizer: "info",
      "org admin": "warning",
    };
    return <Badge bg={variants[role] || "secondary"}>{role || "user"}</Badge>;
  };

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Card className="mb-4" style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        border: "none",
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
      }}>
        <Card.Body className="text-white">
          <Row className="align-items-center">
            <Col>
              <h2 className="mb-0">
                <i className="fas fa-users me-2"></i>
                User Management
              </h2>
              <p className="mb-0 opacity-75">Manage and view user information, events, and registrations</p>
            </Col>
            <Col xs="auto">
              <Button
                variant="light"
                onClick={loadUsers}
                className="me-2"
              >
                <i className="fas fa-sync-alt me-1"></i>
                Refresh
              </Button>
              <CSVLink
                data={exportUsersToCSV()}
                filename={`users-${new Date().toISOString().split('T')[0]}.csv`}
                className="btn btn-light"
              >
                <i className="fas fa-download me-1"></i>
                Export CSV
              </CSVLink>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-4" style={{
        border: "none",
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        borderRadius: "12px"
      }}>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Search Users</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="fas fa-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by email, name, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fw-bold">Status Filter</Form.Label>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending">Pending</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fw-bold">Results</Form.Label>
                <div className="form-control-plaintext">
                  {filteredUsers.length} users found
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Analytics Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center" style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white"
          }}>
            <Card.Body>
              <h3>{userAnalytics.totalUsers}</h3>
              <p className="mb-0">Total Registered Users</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center" style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
            color: "white"
          }}>
            <Card.Body>
              <h3>{userAnalytics.activeUsers.daily}</h3>
              <p className="mb-0">Active Users (Today)</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center" style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            color: "white"
          }}>
            <Card.Body>
              <h3>{userAnalytics.newUsers.today}</h3>
              <p className="mb-0">New Users (Today)</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center" style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
            color: "white"
          }}>
            <Card.Body>
              <h3>{userAnalytics.newUsers.thisMonth}</h3>
              <p className="mb-0">New Users (This Month)</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Detailed Analytics */}
      <Row className="mb-4">
        <Col md={6}>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <h6 className="mb-3">Active Users Overview</h6>
              <Row>
                <Col md={4} className="text-center">
                  <div className="p-3 border rounded">
                    <h4 className="text-primary">{userAnalytics.activeUsers.daily}</h4>
                    <small className="text-muted">Daily</small>
                  </div>
                </Col>
                <Col md={4} className="text-center">
                  <div className="p-3 border rounded">
                    <h4 className="text-success">{userAnalytics.activeUsers.weekly}</h4>
                    <small className="text-muted">Weekly</small>
                  </div>
                </Col>
                <Col md={4} className="text-center">
                  <div className="p-3 border rounded">
                    <h4 className="text-info">{userAnalytics.activeUsers.monthly}</h4>
                    <small className="text-muted">Monthly</small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <h6 className="mb-3">New Users by Period</h6>
              <Row>
                <Col md={4} className="text-center">
                  <div className="p-3 border rounded">
                    <h4 className="text-warning">{userAnalytics.newUsers.today}</h4>
                    <small className="text-muted">Today</small>
                  </div>
                </Col>
                <Col md={4} className="text-center">
                  <div className="p-3 border rounded">
                    <h4 className="text-danger">{userAnalytics.newUsers.thisWeek}</h4>
                    <small className="text-muted">This Week</small>
                  </div>
                </Col>
                <Col md={4} className="text-center">
                  <div className="p-3 border rounded">
                    <h4 className="text-secondary">{userAnalytics.newUsers.thisMonth}</h4>
                    <small className="text-muted">This Month</small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Users by Role */}
      <Row className="mb-4">
        <Col md={12}>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <h6 className="mb-3">Users by Role</h6>
              <Row>
                {Object.entries(userAnalytics.usersByRole).map(([role, count]) => (
                  <Col md={3} key={role} className="mb-3">
                    <div className="p-3 border rounded text-center">
                      <h5 className="mb-1">{count}</h5>
                      <small className="text-muted text-capitalize">{role}</small>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* User Trend Chart */}
      <Row className="mb-4">
        <Col md={12}>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <h6 className="mb-3">User Registration Trend (Last 30 Days)</h6>
              <div style={{ height: "300px", overflowX: "auto" }}>
                <div className="d-flex align-items-end" style={{ height: "250px", gap: "2px" }}>
                  {userAnalytics.userTrend.map((day, index) => (
                    <div
                      key={day.date}
                      className="bg-primary"
                      style={{
                        width: "20px",
                        height: `${Math.max(day.count * 10, 5)}px`,
                        minHeight: "5px",
                        borderRadius: "2px 2px 0 0"
                      }}
                      title={`${day.date}: ${day.count} users`}
                    />
                  ))}
                </div>
                <div className="d-flex justify-content-between mt-2">
                  <small className="text-muted">
                    {userAnalytics.userTrend[0]?.date}
                  </small>
                  <small className="text-muted">
                    {userAnalytics.userTrend[userAnalytics.userTrend.length - 1]?.date}
                  </small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Monthly User Analytics */}
      <Row className="mb-4">
        <Col md={12}>
          <Card
            style={{
              border: "none",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              borderRadius: "12px",
            }}
          >
            <Card.Body>
              <h6 className="mb-3">Monthly New Users</h6>
              {userAnalytics.monthlyNewUsers.length === 0 ? (
                <p className="text-muted mb-0 small">
                  No timestamp data available for monthly analytics.
                </p>
              ) : (
                <div className="table-responsive">
                  <Table hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th className="text-end">New Users</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userAnalytics.monthlyNewUsers.map((row) => (
                        <tr key={row.key}>
                          <td>{row.label}</td>
                          <td className="text-end">{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Users Table */}
      <Card style={{
        border: "none",
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        borderRadius: "12px"
      }}>
        <Card.Body>
          <Table responsive hover className="mb-0">
            <thead style={{
              background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)"
            }}>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Role</th>
                <th>Created</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.map((user) => (
                <tr key={user.id} style={{ cursor: "pointer" }} onClick={() => handleUserClick(user)}>
                  <td>
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        {user.profileURL ? (
                          <img
                            src={user.profileURL}
                            alt={(user.firstName || "") + " " + (user.lastName || "")}
                            className="rounded-circle"
                            width="40"
                            height="40"
                          />
                        ) : (
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center text-white"
                            style={{
                              width: "40px",
                              height: "40px",
                              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                            }}
                          >
                            {user.firstName?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || "U"}
                          </div>
                        )}
                      </div>
                                              <div>
                          <div className="fw-bold">
                            <Link 
                              to={`/user/${user.id}`}
                              className="text-decoration-none"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {user.firstName || "No Name"}
                            </Link>
                          </div>
                          <small className="text-muted">ID: {user.id}</small>
                        </div>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>{user.phone || "N/A"}</td>
                  <td>{getStatusBadge(user.status)}</td>
                  <td>{getRoleBadge(user.orgId ? "org admin" : user.role)}</td>
                  <td>{user.createdAt?.toLocaleDateString() || "N/A"}</td>
                  <td>{user.lastLogin?.toLocaleDateString() || "Never"}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUserClick(user);
                      }}
                    >
                      <i className="fas fa-eye me-1"></i>
                      View Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination>
                <Pagination.First
                  onClick={() => paginate(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                />
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => paginate(page)}
                    >
                      {page}
                    </Pagination.Item>
                  );
                })}
                
                <Pagination.Next
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last
                  onClick={() => paginate(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* User Details Modal */}
      <Modal
        show={showUserModal}
        onHide={() => setShowUserModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white"
        }}>
          <Modal.Title>
            <i className="fas fa-user me-2"></i>
            User Details: {selectedUser?.displayName || selectedUser?.email}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
              <Row>
                <Col md={3}>
                  <Nav variant="pills" className="flex-column">
                    <Nav.Item>
                      <Nav.Link eventKey="profile">Profile</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="events">Events Attended</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="registrations">Registrations</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="orders">Orders</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Col>
                <Col md={9}>
                  <Tab.Content>
                    <Tab.Pane eventKey="profile">
                      <Card>
                        <Card.Body>
                          <Row>
                            <Col md={4} className="text-center">
                              {selectedUser.profileURL ? (
                                <img
                                  src={selectedUser.profileURL}
                                  alt={(selectedUser.firstName || "") + " " + (selectedUser.lastName || "")}
                                  className="rounded-circle mb-3"
                                  width="120"
                                  height="120"
                                />
                              ) : (
                                <div
                                  className="rounded-circle d-flex align-items-center justify-content-center text-white mx-auto mb-3"
                                  style={{
                                    width: "120px",
                                    height: "120px",
                                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    fontSize: "3rem"
                                  }}
                                >
                                  {selectedUser.firstName?.charAt(0)?.toUpperCase() || selectedUser.email?.charAt(0)?.toUpperCase() || "U"}
                                </div>
                              )}
                              <h5>{(selectedUser.firstName || "") + " " + (selectedUser.lastName || "")}</h5>
                              <p className="text-muted">{selectedUser.email}</p>
                              {getStatusBadge(selectedUser.status)} {getRoleBadge(selectedUser.role)}
                            </Col>
                            <Col md={8}>
                              <h6>User Information</h6>
                              <Table borderless>
                                <tbody>
                                  <tr>
                                    <td><strong>User ID:</strong></td>
                                    <td>{selectedUser.id}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Email:</strong></td>
                                    <td>{selectedUser.email}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Phone:</strong></td>
                                    <td>{selectedUser.phone || "N/A"}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Organization:</strong></td>
                                    <td>{selectedUser.userOrg || "N/A"}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Created:</strong></td>
                                    <td>{selectedUser.createdAt?.toLocaleString() || "N/A"}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Last Login:</strong></td>
                                    <td>{selectedUser.lastLogin?.toLocaleString() || "Never"}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Email Verified:</strong></td>
                                    <td>
                                      <Badge bg={selectedUser.emailVerified ? "success" : "warning"}>
                                        {selectedUser.emailVerified ? "Yes" : "No"}
                                      </Badge>
                                    </td>
                                  </tr>
                                </tbody>
                              </Table>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="events">
                      <Card>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6>Events Attended ({userEvents.length})</h6>
                            <CSVLink
                              data={userEvents.map(event => ({
                                Event: event.eventTitle || "N/A",
                                Action: event.action || "N/A",
                                Timestamp: event.timestamp?.toLocaleString() || "N/A",
                                IP: event.ip || "N/A"
                              }))}
                              filename={`user-events-${selectedUser.id}.csv`}
                              className="btn btn-sm btn-outline-primary"
                            >
                              Export
                            </CSVLink>
                          </div>
                          {userEvents.length > 0 ? (
                            <Table responsive>
                              <thead>
                                <tr>
                                  <th>Event</th>
                                  <th>Action</th>
                                  <th>Timestamp</th>
                                  <th>IP Address</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userEvents.map((event) => (
                                  <tr key={event.id}>
                                    <td>{event.eventTitle || "N/A"}</td>
                                    <td>
                                      <Badge bg="info">{event.action || "N/A"}</Badge>
                                    </td>
                                    <td>{event.timestamp?.toLocaleString() || "N/A"}</td>
                                    <td>{event.ip || "N/A"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ) : (
                            <Alert variant="info">No events found for this user.</Alert>
                          )}
                        </Card.Body>
                      </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="registrations">
                      <Card>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6>Event Registrations ({userRegistrations.length})</h6>
                            <CSVLink
                              data={userRegistrations.map(reg => ({
                                Event: reg.eventTitle || "N/A",
                                Status: reg.status || "N/A",
                                "Registration Date": reg.createdAt?.toLocaleString() || "N/A",
                                "Check-in": reg.checkInTime?.toLocaleString() || "N/A"
                              }))}
                              filename={`user-registrations-${selectedUser.id}.csv`}
                              className="btn btn-sm btn-outline-primary"
                            >
                              Export
                            </CSVLink>
                          </div>
                          {userRegistrations.length > 0 ? (
                            <Table responsive>
                              <thead>
                                <tr>
                                  <th>Event</th>
                                  <th>Status</th>
                                  <th>Registration Date</th>
                                  <th>Check-in Time</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userRegistrations.map((reg) => (
                                  <tr key={reg.id}>
                                    <td>{reg.eventTitle || "N/A"}</td>
                                    <td>
                                      <Badge bg={reg.status === "confirmed" ? "success" : "warning"}>
                                        {reg.status || "pending"}
                                      </Badge>
                                    </td>
                                    <td>{reg.createdAt?.toLocaleString() || "N/A"}</td>
                                    <td>{reg.checkInTime?.toLocaleString() || "Not checked in"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ) : (
                            <Alert variant="info">No registrations found for this user.</Alert>
                          )}
                        </Card.Body>
                      </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="orders">
                      <Card>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6>Orders ({userOrders.length})</h6>
                            <CSVLink
                              data={userOrders.map(order => ({
                                "Order ID": order.orderId || "N/A",
                                Event: order.eventTitle || "N/A",
                                Amount: order.totalAmount || 0,
                                Status: order.status || "N/A",
                                "Order Date": order.createdAt?.toLocaleString() || "N/A"
                              }))}
                              filename={`user-orders-${selectedUser.id}.csv`}
                              className="btn btn-sm btn-outline-primary"
                            >
                              Export
                            </CSVLink>
                          </div>
                          {userOrders.length > 0 ? (
                            <Table responsive>
                              <thead>
                                <tr>
                                  <th>Order ID</th>
                                  <th>Event</th>
                                  <th>Amount</th>
                                  <th>Status</th>
                                  <th>Order Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userOrders.map((order) => (
                                  <tr key={order.id}>
                                    <td>{order.orderId || "N/A"}</td>
                                    <td>{order.eventTitle || "N/A"}</td>
                                    <td>${order.totalAmount || 0}</td>
                                    <td>
                                      <Badge bg={order.status === "completed" ? "success" : "warning"}>
                                        {order.status || "pending"}
                                      </Badge>
                                    </td>
                                    <td>{order.createdAt?.toLocaleString() || "N/A"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ) : (
                            <Alert variant="info">No orders found for this user.</Alert>
                          )}
                        </Card.Body>
                      </Card>
                    </Tab.Pane>
                  </Tab.Content>
                </Col>
              </Row>
            </Tab.Container>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
} 